/**
 * Notice Action Handlers
 * Handles actions embedded in user notices (accept/decline/block)
 */

import { socialApi } from '../endpoints/social';
import { sessionsApi } from '../endpoints/sessions';
import { NoticeTypeIds, type FriendRequestData, type GroupInvitationData, type SessionInviteNoticeData } from '../types/notices';

/**
 * Execute a notice action based on the action type
 */
export async function executeNoticeAction(
  noticeType: string,
  actionType: string,
  actionData: string,
  parsedData?: any
): Promise<void> {
  console.log('Executing notice action:', { noticeType, actionType, actionData, parsedData });

  switch (noticeType.toUpperCase()) {
    case NoticeTypeIds.FRIEND_REQUEST.toUpperCase():
      await handleFriendRequestAction(actionType, actionData, parsedData);
      break;

    case NoticeTypeIds.GROUP_INVITATION.toUpperCase():
      await handleGroupInvitationAction(actionType, actionData, parsedData);
      break;

    case NoticeTypeIds.ADVENTURE_INVITATION.toUpperCase():
      await handleAdventureInvitationAction(actionType, actionData, parsedData);
      break;

    case NoticeTypeIds.SESSION_INVITE.toUpperCase():
      await handleSessionInviteAction(actionType, actionData, parsedData);
      break;

    case NoticeTypeIds.MODERATOR_APPROVAL_REQUEST.toUpperCase():
      // Navigation-only action handled at the UI layer
      return;

    default:
      console.warn('Unknown notice type for action:', noticeType);
      throw new Error(`Unsupported notice type: ${noticeType}`);
  }
}

/**
 * Handle friend request actions
 */
async function handleFriendRequestAction(
  actionType: string,
  actionData: string,
  parsedData?: FriendRequestData
): Promise<void> {
  const connectionId = parsedData?.connectionRequestId || actionData;

  switch (actionType) {
    case 'accept_friend_request':
      await socialApi.acceptFriendRequest(connectionId);
      break;

    case 'decline_friend_request':
      await socialApi.rejectFriendRequest(connectionId);
      break;

    case 'block_user':
      const userId = parsedData?.requesterId;
      if (!userId) {
        throw new Error('User ID not found in friend request data');
      }
      await socialApi.blockUser({ userId });
      break;

    default:
      throw new Error(`Unknown friend request action: ${actionType}`);
  }
}

/**
 * Handle group invitation actions
 */
async function handleGroupInvitationAction(
  actionType: string,
  actionData: string,
  parsedData?: GroupInvitationData
): Promise<void> {
  const groupId = parsedData?.groupId || actionData;

  switch (actionType) {
    case 'accept_group_invite':
      await socialApi.acceptGroupInvite(groupId);
      break;

    case 'decline_group_invite':
      await socialApi.declineGroupInvite(groupId);
      break;

    case 'block_group':
      await socialApi.blockGroup(groupId);
      break;

    default:
      throw new Error(`Unknown group invitation action: ${actionType}`);
  }
}

/**
 * Handle adventure invitation actions
 */
async function handleAdventureInvitationAction(
  actionType: string,
  actionData: string,
  parsedData?: any
): Promise<void> {
  // TODO: Implement when adventure invitation endpoints are available
  console.warn('Adventure invitation actions not yet implemented');
  throw new Error('Adventure invitation functionality not yet available');
}

/**
 * Handle session invite actions
 * The actionData from the notice is the sessionId.
 * Accept uses joinSession since the backend resolves the invite from the authenticated user context.
 */
async function handleSessionInviteAction(
  actionType: string,
  actionData: string,
  parsedData?: SessionInviteNoticeData
): Promise<void> {
  const sessionId = parsedData?.SessionId || actionData;

  if (!sessionId) {
    throw new Error('Session ID not found in session invite data');
  }

  switch (actionType) {
    case 'accept_session_invite':
      await sessionsApi.joinSession(sessionId);
      break;

    case 'decline_session_invite':
      await sessionsApi.declineMyInvite(sessionId);
      break;

    default:
      throw new Error(`Unknown session invite action: ${actionType}`);
  }
}

/**
 * Get user-friendly action button variant
 */
export function getActionButtonVariant(actionType: string): 'contained' | 'outlined' | 'text' {
  if (actionType.includes('accept') || actionType.includes('join')) {
    return 'contained';
  }
  if (actionType.includes('block')) {
    return 'outlined';
  }
  return 'outlined';
}

/**
 * Get user-friendly action button color
 */
export function getActionButtonColor(
  actionType: string
): 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' {
  if (actionType.includes('accept') || actionType.includes('join')) {
    return 'primary';
  }
  if (actionType.includes('block') || actionType.includes('decline') || actionType.includes('deny')) {
    return 'error';
  }
  return 'secondary';
}
